import * as Y from 'yjs';
import { Schema } from '@tiptap/pm/model';
import { prosemirrorJSONToYDoc, yDocToProsemirrorJSON } from '@tiptap/y-tiptap';

/** Same wire schema as the canonical editor; native validation remains authoritative. */
const align = { textAlign: { default: null }, styleId: { default: null }, sectionTitleId: { default: null } };
const cell = { colspan: { default: 1 }, rowspan: { default: 1 }, colwidth: { default: null } };
export const documentCrdtSchema = new Schema({
  nodes: {
    doc: { content: 'block*' }, text: { group: 'inline' },
    paragraph: { group: 'block', content: 'inline*', attrs: align },
    heading: { group: 'block', content: 'inline*', attrs: { ...align, level: { default: 2 }, sectionId: { default: null } } },
    sectionBoundary: { group: 'block', atom: true, attrs: { sectionId: { default: null } } },
    blockquote: { group: 'block', content: 'block+' },
    bulletList: { group: 'block', content: 'listItem+' },
    orderedList: { group: 'block', content: 'listItem+', attrs: { start: { default: 1 } } },
    listItem: { content: 'paragraph block*' },
    codeBlock: { group: 'block', content: 'text*', marks: '', attrs: { language: { default: null } } },
    horizontalRule: { group: 'block', atom: true }, hardBreak: { group: 'inline', inline: true },
    image: { group: 'block', atom: true, attrs: { src: {}, alt: { default: null }, title: { default: null }, width: { default: null }, height: { default: null }, textAlign: { default: null } } },
    table: { group: 'block', content: 'tableRow+', attrs: {} }, tableRow: { content: '(tableCell | tableHeader)+' },
    tableCell: { content: 'block+', attrs: cell }, tableHeader: { content: 'block+', attrs: cell },
    pageBreak: { group: 'block', atom: true }, tableOfContents: { group: 'block', atom: true },
    layoutToken: { group: 'inline', inline: true, atom: true, attrs: { kind: { default: 'title' } } }
  },
  marks: {
    bold: {}, italic: {}, underline: {}, strike: {}, code: { excludes: '_' },
    link: { attrs: { href: {}, target: { default: '_blank' }, rel: { default: 'noopener noreferrer nofollow' }, class: { default: null } }, inclusive: false },
    trackedInsertion: { attrs: { changeId: {} }, inclusive: false, excludes: 'trackedInsertion trackedDeletion' },
    trackedDeletion: { attrs: { changeId: {} }, inclusive: false, excludes: 'trackedInsertion trackedDeletion' }
  }
});
export const CRDT_FIELD = 'prosemirror';
export const REMOTE_DOCUMENT_ORIGIN = 'native-document-remote';
export function createDocumentCrdt(content: { type: string; content: unknown[] }) {
  return prosemirrorJSONToYDoc(documentCrdtSchema, { type: 'doc', content: content.content }, CRDT_FIELD);
}
export function projectDocumentCrdt(doc: Y.Doc) {
  const json = yDocToProsemirrorJSON(doc, CRDT_FIELD);
  documentCrdtSchema.nodeFromJSON(json).check();
  return json as { type: 'doc'; content: Array<Record<string, unknown>> };
}
export function encodeDocumentBytes(bytes: Uint8Array): string {
  // Browser and Node share this bounded codec, without Buffer in the browser bundle.
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}
export function decodeDocumentBytes(value: string, max = 4 * 1024 * 1024): Uint8Array {
  if (value.length > Math.ceil(max / 3) * 4 || !/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(value)) throw new Error('Invalid or oversized collaboration data.');
  const binary = atob(value);
  if (binary.length > max) throw new Error('Collaboration data exceeds its limit.');
  return Uint8Array.from(binary, char => char.charCodeAt(0));
}
export function restoreDocumentCrdt(update: string) {
  const doc = new Y.Doc();
  try { Y.applyUpdate(doc, decodeDocumentBytes(update), REMOTE_DOCUMENT_ORIGIN); return doc; }
  catch (error) { doc.destroy(); throw error; }
}
export function documentCrdtState(doc: Y.Doc) {
  return { update: encodeDocumentBytes(Y.encodeStateAsUpdate(doc)), vector: encodeDocumentBytes(Y.encodeStateVector(doc)) };
}
